import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import { CircularProgress } from '@mui/joy'
import { EXISTING_DOCUMENT_SOURCES, ExistingDocumentRow, ExistingDocumentSource, SelectedDocument, apiErrorMessage, documentProcessingTestApi } from '../api'

const truncate = (text: string | null | undefined, maxLength = 40): string => (!text ? '' : text.length <= maxLength ? text : `${text.slice(0, maxLength)}…`)

interface Props {
    onSelect: (doc: SelectedDocument) => void
}

/**
 * Tab 2 — real documents already sitting in this system's own DB/GCS, across
 * every doc type the pipeline can classify (not just ratecons). Nothing here
 * is new/synthetic data — each source is the same collection/bucket the
 * document's owning feature (driver CDL upload, load photo-proofs, fleet
 * work orders, ratecon-processor) already writes to; this only reads.
 *
 * None of these predate ADR-022, so none have a stored contentHash — picking
 * one calls /resolve-external/v1 first, a brief server-side stream-hash.
 */
export default function ExistingDocumentsPicker({ onSelect }: Props) {
    const [source, setSource] = React.useState<ExistingDocumentSource>('ratecon')
    const [projectName, setProjectName] = React.useState('')
    const [rows, setRows] = React.useState<ExistingDocumentRow[]>([])
    const [loading, setLoading] = React.useState(false)
    const [resolvingId, setResolvingId] = React.useState<string | null>(null)
    const [error, setError] = React.useState<string | null>(null)

    const sourceMeta = EXISTING_DOCUMENT_SOURCES.find((s) => s.value === source)!
    const canLoad = !sourceMeta.requiresProjectName || !!projectName.trim()

    // Deliberately NOT memoized with useCallback — it needs to always see the
    // current projectName/source, and this component is a local dev tool with
    // no render-cost reason to memoize. A stale useCallback([source]) closure
    // here previously froze canLoad/projectName at whatever they were the last
    // time `source` changed, silently no-op'ing every Load click afterward.
    const load = async (currentSource: ExistingDocumentSource, currentProjectName: string) => {
        setLoading(true)
        setError(null)
        try {
            setRows(await documentProcessingTestApi.listExistingDocuments(currentSource, currentProjectName.trim() || undefined, 15))
        } catch (err) {
            setError(apiErrorMessage(err, `Failed to load ${sourceMeta.label} documents`))
            setRows([])
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        setRows([])
        if (!sourceMeta.requiresProjectName) load(source, '').catch(() => undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source])

    const handleUse = async (row: ExistingDocumentRow) => {
        // The copy is stored under a tenant, like every pipeline document.
        const owner = row.projectName ?? projectName.trim()
        if (!owner) {
            setError('Enter the project name to store this document under')
            return
        }
        setResolvingId(row.id)
        setError(null)
        try {
            const { contentHash, blobUri } = await documentProcessingTestApi.resolveExternal(row.fileUrl, owner)
            onSelect({ contentHash, blobUri, filename: row.label, projectName: owner })
        } catch {
            setError(`Failed to resolve "${truncate(row.label)}" — the document may no longer be accessible`)
        } finally {
            setResolvingId(null)
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <FormControl size="sm" sx={{ minWidth: 220 }}>
                    <FormLabel>Document Type</FormLabel>
                    <Select size="sm" value={source} onChange={(_, val) => val && setSource(val as ExistingDocumentSource)}>
                        {EXISTING_DOCUMENT_SOURCES.map((s) => (
                            <Option key={s.value} value={s.value}>
                                {s.label}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                {sourceMeta.requiresProjectName && (
                    <FormControl size="sm" sx={{ flex: 1, minWidth: 180 }}>
                        <FormLabel>Project Name (required — this type lives in a per-tenant database)</FormLabel>
                        <Input size="sm" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="the tenant's project name" />
                    </FormControl>
                )}
                <Button size="sm" variant="outlined" onClick={() => load(source, projectName)} disabled={loading || !canLoad}>
                    {loading ? <CircularProgress size="sm" /> : 'Load'}
                </Button>
            </Box>

            <Typography level="body-sm" color="neutral">
                Last 15 real {sourceMeta.label.toLowerCase()} documents, most recent first. Picking one streams and hashes the file server-side — takes a
                moment.
            </Typography>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 320 }}>
                <Table size="sm" stickyHeader hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: 260 }}>Label</th>
                            <th style={{ width: 150 }}>Date</th>
                            <th style={{ width: 90 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <Typography level="body-xs">{truncate(row.label)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.date ? new Date(row.date).toLocaleString() : '—'}</Typography>
                                </td>
                                <td>
                                    <Button size="sm" variant="plain" disabled={resolvingId === row.id} onClick={() => handleUse(row)}>
                                        {resolvingId === row.id ? <CircularProgress size="sm" /> : 'Use'}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '24px' }}>
                                    <Typography level="body-sm" color="neutral">
                                        {sourceMeta.requiresProjectName && !projectName.trim()
                                            ? 'Enter a project name and click Load.'
                                            : `No recent ${sourceMeta.label.toLowerCase()} documents found.`}
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    )
}
